import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

function AnimatedSection({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.06 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ opacity: visible ? 1 : 0, transform: visible ? 'none' : 'translateY(30px)', transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s` }}>
      {children}
    </div>
  );
}

const STEPS = [
  {
    num: '01',
    title: 'Audyt & Odkrycie',
    duration: '3–5 dni',
    icon: 'ri-search-eye-line',
    color: '#00d4ff',
    desc: 'Zaczynamy od dogłębnej analizy Twojego biznesu, obecności online i konkurencji. Sprawdzamy techniczny stan strony, strukturę SEO, widoczność w Google i potencjał wdrożenia AI. Nic nie pozostaje bez odpowiedzi.',
    deliverables: [
      'Raport audytu technicznego SEO',
      'Analiza 10 głównych konkurentów',
      'Mapa możliwości wdrożenia AI',
      'Analiza aktualnej widoczności Google',
      'Wstępna propozycja rozwiązań i budżetu',
    ],
    metric: 'Raport w 5 dni roboczych',
  },
  {
    num: '02',
    title: 'Strategia & Planowanie',
    duration: '3–7 dni',
    icon: 'ri-map-2-line',
    color: '#00ff88',
    desc: 'Na podstawie audytu tworzymy kompleksową strategię cyfrową dopasowaną do celów Twojego biznesu. Definiujemy KPIs, harmonogram wdrożenia i pełny zakres prac — zanim zostanie napisana jedna linijka kodu.',
    deliverables: [
      'Dokument strategii cyfrowej (20–30 stron)',
      'Roadmapa wdrożenia z harmonogramem',
      'Pełna specyfikacja techniczna projektu',
      'Definicja KPIs i celów mierzalnych',
      'Plan słów kluczowych i struktury SEO',
    ],
    metric: 'Zatwierdzenie przed startem',
  },
  {
    num: '03',
    title: 'Design & Prototypowanie',
    duration: '5–10 dni',
    icon: 'ri-pencil-ruler-2-line',
    color: '#ff6b9d',
    desc: 'Projektujemy każdy ekran, przepływ użytkownika i architekturę AI zanim zacznie się kodowanie. Interaktywne prototypy w Figma pozwalają zobaczyć finalny produkt i zatwierdzić go przed implementacją.',
    deliverables: [
      'Design System (kolory, typografia, komponenty)',
      'Interaktywne prototypy Figma all breakpoints',
      'Architektura agenta AI i drzewa konwersacji',
      'Mapa strony i struktury URL',
      'Zatwierdzenie projektu — 0 niespodzianek',
    ],
    metric: 'Twoje zatwierdzenie przed kodem',
  },
  {
    num: '04',
    title: 'Implementacja',
    duration: '10–30 dni',
    icon: 'ri-code-s-slash-line',
    color: '#00d4ff',
    desc: 'Budujemy rozwiązanie zgodnie z zatwierdzoną specyfikacją. Regularne checkpointy co 5–7 dni, dostęp do środowiska testowego i pełna transparentność postępów. Pracujemy w krótkich iteracjach.',
    deliverables: [
      'Strona / aplikacja / agent AI',
      'Integracje z CRM, booking, e-mail',
      'Panel zarządzania treścią (CMS)',
      'Dokumentacja techniczna i użytkownika',
      'Środowisko testowe z dostępem klienta',
    ],
    metric: 'Checkpointy co 5–7 dni',
  },
  {
    num: '05',
    title: 'Testy & Optymalizacja',
    duration: '3–7 dni',
    icon: 'ri-test-tube-line',
    color: '#00ff88',
    desc: 'Przed startem projekt przechodzi rygorystyczne testy: wydajność, bezpieczeństwo, cross-browser, UX. Celujemy w wynik Lighthouse 90+ i Core Web Vitals na zielono na wszystkich urządzeniach.',
    deliverables: [
      'Raport Core Web Vitals 90+ (Lighthouse)',
      'Testy na 8 przeglądarkach i 15 urządzeniach',
      'Audyt bezpieczeństwa (XSS, HTTPS, headers)',
      'Testy konwersji i optymalizacja CTA',
      'Testy agenta AI na 100+ scenariuszach',
    ],
    metric: 'Lighthouse 90+ gwarantowany',
  },
  {
    num: '06',
    title: 'Start & Wzrost',
    duration: 'Ongoing',
    icon: 'ri-rocket-2-line',
    color: '#ff6b9d',
    desc: 'Uruchomienie to dopiero początek. Monitorujemy wyniki, optymalizujemy kampanie SEO, uczymy agentów AI na realnych danych i systematycznie poprawiamy wszystkie wskaźniki — miesiąc do miesiąca.',
    deliverables: [
      'Wdrożenie produkcyjne z monitoringiem',
      'Miesięczny raport SEO i wyników AI',
      'Optymalizacja kampanii na podstawie danych',
      'Aktualizacje i uczenie agentów AI',
      'Wsparcie techniczne i proaktywny rozwój',
    ],
    metric: 'Miesięczny raport wyników',
  },
];

const GUARANTEES = [
  { icon: 'ri-time-line', title: 'Gwarancja terminu', desc: 'Jeśli przekroczymy uzgodniony termin z naszej winy — zwracamy 10% wartości projektu za każdy tydzień opóźnienia.', color: '#00d4ff' },
  { icon: 'ri-bar-chart-line', title: 'Lighthouse 90+', desc: 'Każda strona opuszcza nasze studio z wynikiem Core Web Vitals 90+ w Lighthouse. Inaczej optymalizujemy za darmo.', color: '#00ff88' },
  { icon: 'ri-customer-service-2-line', title: 'Wsparcie po starcie', desc: 'Każdy projekt obejmuje wsparcie po starcie — bez dodatkowych kosztów przez umówiony czas opieki.', color: '#ff6b9d' },
  { icon: 'ri-shield-check-line', title: 'Zero niespodzianek', desc: 'Cena ustalona w specyfikacji to cena finalna. Żadnych "a przy okazji dopłata za..." w połowie projektu.', color: '#00d4ff' },
];

const FAQS = [
  {
    q: 'Ile czasu zajmuje cały proces od pierwszego spotkania do uruchomienia?',
    a: 'Dla typowej strony premium z agentem AI — od 5 do 8 tygodni. Proste projekty (landing page, wizytówka) mogą być gotowe w 2–3 tygodnie. Kompleksowe wdrożenia enterprise z dedykowanym oprogramowaniem AI — 3–6 miesięcy. Na pierwszej konsultacji podajemy dokładny harmonogram dla Twojego projektu.',
  },
  {
    q: 'Jak wygląda moje zaangażowanie w trakcie projektu?',
    a: 'Minimalizujemy Twój czas. Na początku projektu potrzebujemy ok. 2–4 godzin na warsztaty odkrycia i zatwierdzenie strategii. Później regularne checkpointy trwają 30–45 minut tygodniowo. Całą techniczną robotę bierzemy na siebie — Ty zatwierdzasz kierunek, my dostarczamy.',
  },
  {
    q: 'Czy po uruchomieniu zostajecie z nami?',
    a: 'Tak — to jest nasza standardowa praktyka. Większość klientów decyduje się na pakiety opieki miesięcznej, w ramach których prowadzimy SEO, rozwijamy agentów AI na podstawie realnych danych i reagujemy na potrzeby techniczne. Długoterminowa współpraca daje najlepsze, skumulowane efekty.',
  },
  {
    q: 'Co jeśli projekt nie spełni moich oczekiwań?',
    a: 'Przed startem precyzyjnie definiujemy zakres i kryteria akceptacji w specyfikacji technicznej. Pracujemy w krótkich iteracjach z regularnymi zatwierdzeniami, więc niespodzianki praktycznie nie istnieją. Jeśli coś nie działa zgodnie z ustaleniami — naprawiamy bezpłatnie.',
  },
  {
    q: 'Jak mierzcie wyniki swoich wdrożeń?',
    a: 'Każdy projekt ma zdefiniowane KPIs przed startem — mogą to być pozycje w Google, współczynnik konwersji, liczba rezerwacji, czas obsługi zapytania przez AI lub przychód z bezpośrednich rezerwacji. Raportujemy regularnie, porównując do punktu bazowego ustalonego przed wdrożeniem.',
  },
];

export default function ProcesPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [activeStep, setActiveStep] = useState(0);

  return (
    <div className="min-h-screen bg-[#080808] text-white overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 pb-20 px-4 md:px-6 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(0,212,255,0.08) 0%, transparent 60%)' }}
        />
        <div className="max-w-[1100px] mx-auto text-center relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#00d4ff]/30 bg-[#00d4ff]/5 backdrop-blur-sm mb-8">
              <span className="w-2 h-2 rounded-full bg-[#00d4ff] animate-pulse" />
              <span className="text-[#00d4ff] text-xs font-semibold tracking-widest uppercase">Jak pracujemy</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-tight mb-8">
              Sprawdzony proces,<br />
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #00d4ff, #00ff88)' }}>
                przewidywalne wyniki
              </span>
            </h1>
            <p className="text-xl text-white/45 max-w-3xl mx-auto leading-relaxed">
              Nie działamy intuicyjnie. Każdy projekt realizujemy według sprawdzonego 6-etapowego procesu, który minimalizuje ryzyko, maksymalizuje wyniki i zawsze dostarcza na czas.
            </p>
            <div className="flex flex-wrap gap-8 justify-center mt-10">
              {[
                { val: '6', label: 'Etapów procesu' },
                { val: '5–9 tyg.', label: 'Typowy czas realizacji' },
                { val: '100%', label: 'Projektów na czas' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-3xl font-black text-[#00d4ff]">{s.val}</div>
                  <div className="text-white/35 text-xs mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Steps overview interactive */}
      <section className="pb-8 px-4 md:px-6">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex gap-1 md:gap-2 overflow-x-auto pb-3 scrollbar-hide">
            {STEPS.map((s, idx) => (
              <button
                key={s.num}
                onClick={() => setActiveStep(idx)}
                className="flex-shrink-0 flex flex-col items-center gap-1.5 px-4 py-3 rounded-xl border transition-all duration-250 cursor-pointer whitespace-nowrap"
                style={{
                  background: activeStep === idx ? `${s.color}12` : 'rgba(255,255,255,0.03)',
                  borderColor: activeStep === idx ? `${s.color}40` : 'rgba(255,255,255,0.08)',
                }}
              >
                <span className="text-[10px] font-black" style={{ color: activeStep === idx ? s.color : 'rgba(255,255,255,0.3)' }}>{s.num}</span>
                <span className="text-xs font-semibold" style={{ color: activeStep === idx ? 'white' : 'rgba(255,255,255,0.45)' }}>{s.title}</span>
                <span className="text-[10px]" style={{ color: activeStep === idx ? s.color : 'rgba(255,255,255,0.25)' }}>{s.duration}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Steps detailed */}
      <section className="py-8 pb-24 px-4 md:px-6">
        <div className="max-w-[1200px] mx-auto space-y-6">
          {STEPS.map((step, idx) => (
            <AnimatedSection key={step.num} delay={idx * 0.06}>
              <div
                className="p-7 md:p-10 rounded-3xl border transition-all duration-300 cursor-pointer"
                style={{
                  background: activeStep === idx ? `${step.color}06` : 'rgba(255,255,255,0.02)',
                  borderColor: activeStep === idx ? `${step.color}35` : 'rgba(255,255,255,0.08)',
                }}
                onClick={() => setActiveStep(idx)}
              >
                <div className="flex flex-col md:flex-row gap-8">
                  {/* Left */}
                  <div className="flex items-start gap-5 md:w-[280px] flex-shrink-0">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${step.color}12`, border: `1px solid ${step.color}30` }}>
                      <i className={`${step.icon} text-2xl`} style={{ color: step.color }} />
                    </div>
                    <div>
                      <p className="text-4xl font-black mb-0.5 leading-none" style={{ color: `${step.color}25` }}>{step.num}</p>
                      <h3 className="text-xl font-black text-white leading-tight">{step.title}</h3>
                      <div className="flex items-center gap-1.5 mt-2">
                        <i className="ri-time-line text-xs" style={{ color: step.color }} />
                        <span className="text-xs font-semibold" style={{ color: step.color }}>{step.duration}</span>
                      </div>
                      <div className="mt-3 px-3 py-1.5 rounded-lg inline-block text-[10px] font-bold"
                        style={{ background: `${step.color}15`, color: step.color }}>
                        {step.metric}
                      </div>
                    </div>
                  </div>

                  {/* Right */}
                  <div className="flex-1">
                    <p className="text-white/50 leading-relaxed mb-6 text-sm">{step.desc}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      {step.deliverables.map((d) => (
                        <div key={d} className="flex items-start gap-2.5">
                          <i className="ri-checkbox-circle-fill text-sm mt-0.5 flex-shrink-0" style={{ color: step.color }} />
                          <span className="text-white/40 text-xs leading-relaxed">{d}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Guarantees */}
      <section className="py-24 px-4 md:px-6 bg-[#060606] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-[#00d4ff] text-xs uppercase tracking-[3px] font-semibold mb-4">Nasze gwarancje</p>
              <h2 className="text-4xl font-black text-white mb-4">Co gwarantujemy w każdym projekcie</h2>
              <p className="text-white/40 text-base max-w-2xl mx-auto">Nie obietnice — konkretne zobowiązania, które wpisujemy do umowy.</p>
            </div>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {GUARANTEES.map((g, idx) => (
              <AnimatedSection key={g.title} delay={idx * 0.09}>
                <div className="p-8 rounded-2xl border border-white/8 bg-white/[0.02] hover:border-white/16 transition-all h-full">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: `${g.color}12`, border: `1px solid ${g.color}30` }}>
                    <i className={`${g.icon} text-2xl`} style={{ color: g.color }} />
                  </div>
                  <h4 className="font-black text-white text-base mb-3">{g.title}</h4>
                  <p className="text-white/40 text-sm leading-relaxed">{g.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-4 md:px-6">
        <div className="max-w-[1200px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-[#00d4ff] text-xs uppercase tracking-[3px] font-semibold mb-4">Harmonogram</p>
              <h2 className="text-3xl md:text-4xl font-black text-white mb-3">Od briefu do uruchomienia</h2>
              <p className="text-white/40">Przykładowy harmonogram — Strona Premium + Agent AI + SEO</p>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <div className="overflow-x-auto pb-2">
              <div className="flex gap-3 min-w-[700px]">
                {STEPS.map((step) => (
                  <div key={step.num} className="flex-1 text-center">
                    <div className="rounded-xl p-4 mb-3 text-xs font-bold"
                      style={{ background: `${step.color}12`, border: `1px solid ${step.color}25`, color: step.color }}>
                      {step.num}<br />
                      <span className="font-semibold text-[10px]">{step.title}</span>
                    </div>
                    <p className="text-white/25 text-[10px]">{step.duration}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div className="h-full rounded-full" style={{ width: '100%', background: 'linear-gradient(to right, #00d4ff, #00ff88, #ff6b9d, #00d4ff, #00ff88, #ff6b9d)' }} />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 md:px-6 bg-[#060606] border-y border-white/5">
        <div className="max-w-[900px] mx-auto">
          <AnimatedSection>
            <div className="text-center mb-14">
              <p className="text-[#ff6b9d] text-xs uppercase tracking-[3px] font-semibold mb-4">FAQ</p>
              <h2 className="text-4xl font-black text-white mb-4">Pytania o proces</h2>
            </div>
          </AnimatedSection>
          <div className="space-y-2.5">
            {FAQS.map((faq, idx) => (
              <AnimatedSection key={idx} delay={idx * 0.05}>
                <div className="rounded-2xl border border-white/8 overflow-hidden transition-all duration-300"
                  style={{ background: openFaq === idx ? 'rgba(0,212,255,0.03)' : 'rgba(255,255,255,0.02)' }}>
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-6 text-left cursor-pointer">
                    <span className="text-white font-semibold text-sm md:text-base pr-6">{faq.q}</span>
                    <i className={`ri-arrow-down-s-line text-xl transition-transform duration-300 flex-shrink-0 ${openFaq === idx ? 'rotate-180' : ''}`}
                      style={{ color: openFaq === idx ? '#00d4ff' : 'rgba(255,255,255,0.3)' }} />
                  </button>
                  <div style={{ maxHeight: openFaq === idx ? '400px' : '0px', overflow: 'hidden', transition: 'max-height 0.4s ease' }}>
                    <p className="px-6 pb-6 text-white/50 leading-relaxed text-sm">{faq.a}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 md:px-6">
        <div className="max-w-[900px] mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6">Gotowy żeby zacząć?</h2>
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Pierwsza konsultacja jest bezpłatna. Poznajemy Twój biznes, odpowiadamy na pytania i razem projektujemy plan działania — bez zobowiązań.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt"
                className="inline-flex items-center justify-center gap-2 text-black px-10 py-5 rounded-xl font-bold text-lg whitespace-nowrap cursor-pointer hover:scale-105 transition-transform"
                style={{ background: 'linear-gradient(135deg, #00d4ff, #00b8e6)', boxShadow: '0 0 40px rgba(0,212,255,0.25)' }}>
                Umów bezpłatną konsultację
                <i className="ri-arrow-right-line" />
              </Link>
              <Link to="/pakiety"
                className="inline-flex items-center justify-center gap-2 text-white/70 hover:text-white px-10 py-5 rounded-xl font-bold text-lg whitespace-nowrap cursor-pointer border border-white/15 hover:border-white/30 transition-all">
                Zobacz cennik
                <i className="ri-price-tag-3-line" />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
}
